import { useState } from "react";
import { DateTime } from "luxon";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { Pencil } from "lucide-react";
import { Card } from "../../../components/Card";
import { SectionTitle } from "../../../components/SectionTitle";
import { Button } from "../../../components/Button";
import { SidebarPanel } from "../../../components/SidebarPanel";
import { LoadingContainer } from "../../../components/LoadingContainer";
import { SwitchPackageModal } from "./SwitchPackageModal";
import { BillingItemEditForm } from "./BillingItemEditForm";
import { BillingItemMonthlyPrice } from "./BillingItemMonthlyPrice";
import { useGetStoreQuery } from "../../../query/storesQuery";
import { useUpdateBillingItemMutation } from "../../../query/billingItemsQuery";
import { spacing } from "@styling/sizing";
import colors from "@styling/colors";
import { fontSizes } from "@styling/fonts";
import { formatDate, toDateString } from "../../../utils/dateUtils";
import { BillingItemType } from "@KassaroAB/shared/types/enums/BillingItemType";
import { BillingPeriod } from "@KassaroAB/shared/types/enums/BillingPeriod";
import { Currency } from "@KassaroAB/shared/types/enums/currency";
import type { RestBillingItem } from "@KassaroAB/shared/types/billingItem";
import type { RestAvailableBillingItem } from "@KassaroAB/shared/types/availableBillingItem";
import {
  findActivePackageItem,
  getCurrentOrNextPositiveSegment,
  isBillingItemCurrentOrFuture,
} from "../../../utils/billingItemSegments";

const RECENTLY_ENDED_PACKAGE_DAYS = 30;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${spacing.x2}px;
  margin-bottom: ${spacing.x2}px;
  flex-wrap: wrap;
`;

const PackageList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.x1}px;
`;

const PackageRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${spacing.x2}px;
  width: 100%;
  padding: ${spacing.x2}px;
  border: 1px solid ${colors.border};
  border-radius: 8px;
  background-color: ${colors.background};
`;

const PackageInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.x05}px;
  min-width: 0;
`;

const PackageName = styled.div`
  font-size: ${fontSizes.regular}px;
  font-weight: 600;
  color: ${colors.text};
`;

const PackageMeta = styled.div`
  font-size: ${fontSizes.small}px;
  color: ${colors.textMuted};
`;

const PackageActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.x1}px;
`;

const IconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  color: ${colors.textMuted};
  cursor: pointer;
  padding: ${spacing.x05}px;
  border-radius: 4px;

  &:hover {
    background-color: ${colors.backgroundDark};
    color: ${colors.text};
  }
`;

const EmptyText = styled.p`
  margin: 0;
  color: ${colors.textMuted};
`;

const Badge = styled.span`
  font-size: ${fontSizes.small}px;
  font-weight: 500;
  padding: ${spacing.x05}px ${spacing.x1}px;
  margin-left: ${spacing.x1}px;
  border-radius: 4px;
  background-color: ${colors.infoBackground};
  color: ${colors.info};
`;

const EndedBadge = styled(Badge)`
  background-color: ${colors.dangerBackground};
  color: ${colors.danger};
`;

const isPackageRecentlyEnded = (
  billingItem: RestBillingItem,
  today: string,
  endedSince: string
): boolean =>
  billingItem.endDate !== null &&
  billingItem.endDate < today &&
  billingItem.endDate >= endedSince;

interface ActivePackageSectionProps {
  billingItems: RestBillingItem[] | undefined;
  availableBillingItems: RestAvailableBillingItem[] | undefined;
  isLoading?: boolean;
  billingPeriod: BillingPeriod;
  startDate: string;
}

export const ActivePackageSection = ({
  billingItems,
  availableBillingItems,
  isLoading = false,
  billingPeriod,
}: ActivePackageSectionProps) => {
  const { t } = useTranslation();
  const { storeId } = useParams<{ storeId: string }>();
  const today = toDateString(new Date());
  const endedSince =
    DateTime.fromISO(today)
      .minus({ days: RECENTLY_ENDED_PACKAGE_DAYS })
      .toISODate() ?? today;
  const { data: store } = useGetStoreQuery(storeId);
  const storeCurrency = store?.currency ?? Currency.SEK;
  const [isSwitchPackageOpen, setIsSwitchPackageOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<RestBillingItem | null>(null);
  const { mutate: updateBillingItem, isPending: isUpdating } =
    useUpdateBillingItemMutation(storeId ?? null);

  const lookup = new Map((availableBillingItems || []).map((item) => [item.id, item]));
  const activePackageItem = findActivePackageItem(billingItems, today);
  const displayedPackageItems = (billingItems ?? [])
    .filter(
      (billingItem) =>
        billingItem.type === BillingItemType.PACKAGE &&
        (isBillingItemCurrentOrFuture(billingItem, today) ||
          isPackageRecentlyEnded(billingItem, today, endedSince))
    )
    .sort((left, right) => left.startDate.localeCompare(right.startDate));
  const hasCurrentOrFuturePackage = displayedPackageItems.some((billingItem) =>
    isBillingItemCurrentOrFuture(billingItem, today)
  );
  const selectedAvailable = selectedItem
    ? lookup.get(selectedItem.availableBillingItemId)
    : undefined;

  return (
    <Card>
      <SectionHeader>
        <SectionTitle>{t("packageSelection.activePackage")}</SectionTitle>
        <Button onClick={() => setIsSwitchPackageOpen(true)}>
          {activePackageItem
            ? t("packageSelection.changePackage")
            : t("packageSelection.selectPackage")}
        </Button>
      </SectionHeader>

      {isLoading ? (
        <LoadingContainer message={t("billingItems.loading")} />
      ) : displayedPackageItems.length > 0 ? (
        <PackageList>
          {!hasCurrentOrFuturePackage && (
            <EmptyText>{t("packageSelection.noActivePackage")}</EmptyText>
          )}
          {displayedPackageItems.map((packageItem) => {
            const availablePackage = lookup.get(
              packageItem.availableBillingItemId
            );
            const displaySegment = getCurrentOrNextPositiveSegment(
              packageItem,
              today
            );
            const isEnded = !isBillingItemCurrentOrFuture(packageItem, today);
            const isUpcoming =
              (displaySegment?.startDate ?? packageItem.startDate) > today;
            const monthlyPrice =
              availablePackage?.pricing?.monthlyPrice?.currencies?.[
                storeCurrency
              ] ??
              Object.values(
                availablePackage?.pricing?.monthlyPrice?.currencies ?? {}
              ).find((money) => money != null);
            return (
              <PackageRow key={packageItem.id}>
                <PackageInfo>
                  <PackageName>
                    {availablePackage?.name ??
                      packageItem.availableBillingItemId}
                    {packageItem.isDemo && (
                      <Badge>{t("billingItems.edit.isDemo")}</Badge>
                    )}
                    {isEnded && (
                      <EndedBadge>{t("billingItems.status.ended")}</EndedBadge>
                    )}
                    {isUpcoming && (
                      <Badge>{t("billingItems.status.upcoming")}</Badge>
                    )}
                  </PackageName>
                  <PackageMeta>
                    {t(
                      `billingItems.billingPeriod.${
                        packageItem.billingPeriod ?? BillingPeriod.MONTHLY
                      }`
                    )}
                    {" · "}
                    {formatDate(
                      displaySegment?.startDate ?? packageItem.startDate
                    )}
                    {" – "}
                    {displaySegment?.endDate
                      ? formatDate(displaySegment.endDate)
                      : t("entitlements.ongoing")}
                  </PackageMeta>
                </PackageInfo>
                <PackageActions>
                  <BillingItemMonthlyPrice
                    unitPrice={monthlyPrice}
                    discount={packageItem.discount}
                  />
                  <IconButton
                    type="button"
                    aria-label={t("packageSelection.editDetails")}
                    onClick={() => setSelectedItem(packageItem)}
                  >
                    <Pencil size={16} />
                  </IconButton>
                </PackageActions>
              </PackageRow>
            );
          })}
        </PackageList>
      ) : (
        <EmptyText>{t("packageSelection.noActivePackage")}</EmptyText>
      )}

      <SwitchPackageModal
        isOpen={isSwitchPackageOpen}
        onClose={() => setIsSwitchPackageOpen(false)}
        billingItems={billingItems}
        defaultBillingPeriod={billingPeriod}
        defaultStartDate={today}
      />
      {selectedItem && (
        <SidebarPanel
          isOpen
          onClose={() => setSelectedItem(null)}
          title={
            selectedAvailable?.name ?? selectedItem.availableBillingItemId
          }
        >
          <BillingItemEditForm
            billingItem={selectedItem}
            pricing={
              selectedAvailable?.pricing
                ? {
                    monthlyPrice: selectedAvailable.pricing.monthlyPrice,
                    prepaid36MonthPrice:
                      selectedAvailable.pricing.prepaid36MonthPrice,
                    oneTimePrice: selectedAvailable.pricing.oneTimePrice,
                  }
                : undefined
            }
            onSubmit={(data) => {
              updateBillingItem(
                { billingItemId: selectedItem.id, billingItem: data },
                { onSuccess: () => setSelectedItem(null) }
              );
            }}
            onCancel={() => setSelectedItem(null)}
            isSubmitting={isUpdating}
          />
        </SidebarPanel>
      )}
    </Card>
  );
};
